import React, { useContext, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import { Upload, Trash2, FileText, FileSignature, Eye, CheckCircle2 } from 'lucide-react';
import { Dialog, DialogContent } from '../ui/dialog';
import ProviderNdaEsignDialog from './ProviderNdaEsignDialog';
import ProviderW9EsignDialog from './ProviderW9EsignDialog';
import ProviderSelfServeLink from './ProviderSelfServeLink';

// Shared document component — used inside both View (ProviderDetailCard) and Edit (ProviderForm).
// Labeled sections per document type, each with its OWN upload control (not a generic dropdown).
const SECTIONS = [
  { type: 'W-9', required: true, expiry: false, signed: true, note: 'Required. No expiration — you may record the signed date (optional).' },
  { type: 'COI', required: true, expiry: false, note: 'Required. Policy effective/expiration dates are tracked per policy in the Insurance section below.' },
  { type: "Driver's License", required: true, expiry: true, person: true, note: 'Required. Driver\u2019s license for the individual or designated company representative. Record the holder and expiration date.' },
  { type: 'NDA', required: true, signed: true, note: 'Required. Signed NDA / Non-Solicitation agreement. Upload the signed copy; a Global Administrator verifies it.' },
  { type: 'Trade License/Certificate', required: false, expiry: true, note: 'Optional. Add an expiration date if the certificate has one.' },
  { type: 'Other', required: false, expiry: true, note: 'Optional supporting document.' },
];

const todayStr = () => new Date().toISOString().slice(0, 10);
function docState(d) {
  if (d.expiry && d.expiry < todayStr()) return { label: 'Expired', cls: 'bg-rose-100 text-rose-700' };
  if ((d.verification_status || 'pending') === 'verified') return { label: 'Verified', cls: 'bg-emerald-100 text-emerald-700' };
  return { label: 'On File · Awaiting Verification', cls: 'bg-amber-100 text-amber-800' };
}
const slug = (t) => t.replace(/[^a-zA-Z0-9]/g, '');

export default function ProviderDocuments({ provider, token, canManage, onChanged, idPrefix = '' }) {
  const [busyType, setBusyType] = useState('');
  const [draft, setDraft] = useState({});
  const [ndaEsign, setNdaEsign] = useState(false);
  const [w9Esign, setW9Esign] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const docs = provider?.documents || [];
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const fileUrl = (d) => `${API}/service-providers/${provider.id}/documents/${d.id}/file?auth=${token}`;
  const setDraftFor = (type, patch) => setDraft((p) => ({ ...p, [type]: { ...(p[type] || {}), ...patch } }));

  async function onFile(section, e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const d = draft[section.type] || {};
    setBusyType(section.type);
    try {
      const b64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
      // Strip the data: prefix — the ingress WAF blocks base64 data URLs.
      const raw = String(b64).substring(String(b64).indexOf(',') + 1);
      const r = await axios.post(`${API}/service-providers/${provider.id}/documents/upload`, {
        type: section.type, filename: file.name, content_type: file.type, data_base64: raw,
        expiry: section.expiry ? (d.expiry || null) : null,
        person_name: section.person ? (d.person_name || null) : null,
        signed_date: section.signed ? (d.signed_date || null) : null,
      }, h);
      toast.success(`${section.type} uploaded — now On File`);
      setDraftFor(section.type, { expiry: '', person_name: '', signed_date: '' });
      onChanged?.(r.data);
    } catch { toast.error('Upload failed — nothing was changed; try again or enter details manually.'); }
    finally { setBusyType(''); e.target.value = ''; }
  }
  async function verify(d, statusVal) {
    setVerifying(true);
    try { const r = await axios.patch(`${API}/service-providers/${provider.id}/documents/${d.id}`, { verification_status: statusVal }, h); toast.success(`Marked ${statusVal}`); onChanged?.(r.data); setPreviewDoc(null); }
    catch { toast.error('Could not update'); }
    finally { setVerifying(false); }
  }
  async function remove(d) {
    try { const r = await axios.delete(`${API}/service-providers/${provider.id}/documents/${d.id}`, h); toast.success('Removed'); onChanged?.(r.data); }
    catch { toast.error('Could not remove'); }
  }

  return (
    <div data-testid={`${idPrefix}provider-documents`} className="space-y-2">
      {canManage && (
        <ProviderSelfServeLink provider={provider} token={token} onChanged={onChanged} idPrefix={idPrefix} />
      )}
      {SECTIONS.map((section) => {
        const mine = docs.filter((d) => (d.type || '') === section.type);
        const d = draft[section.type] || {};
        const s = slug(section.type);
        return (
          <div key={section.type} className="rounded-md border border-slate-100 p-2" data-testid={`${idPrefix}doc-section-${s}`}>
            <div className="flex items-center gap-2">
              <FileText size={13} className="text-[#0E2A47]" />
              <span className="text-sm font-semibold text-[#0E2A47]">{section.type}</span>
              <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${section.required ? 'bg-[#0E2A47]/10 text-[#0E2A47]' : 'bg-slate-100 text-slate-500'}`}>{section.required ? 'Required' : 'Optional'}</span>
            </div>
            {mine.length > 0 && (
              <div className="mt-1.5 space-y-1">
                {mine.map((doc) => {
                  const st = docState(doc);
                  const isImg = /\.(png|jpe?g|webp|gif|bmp|heic)$/i.test(doc.filename || '');
                  const isPdf = /\.pdf$/i.test(doc.filename || '');
                  return (
                    <div key={doc.id} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`doc-row-${doc.id}`}>
                      <a href={fileUrl(doc)} target="_blank" rel="noreferrer" title="Click to preview or download" data-testid={`doc-thumb-${doc.id}`}
                        className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded border border-slate-200 bg-slate-50 hover:ring-2 hover:ring-[#8FC93A]">
                        {isImg
                          ? <img src={fileUrl(doc)} alt={doc.type} className="h-full w-full object-cover" />
                          : <span className={`text-[9px] font-bold ${isPdf ? 'text-rose-600' : 'text-slate-500'}`}>{isPdf ? 'PDF' : 'FILE'}</span>}
                      </a>
                      <a href={fileUrl(doc)} target="_blank" rel="noreferrer" className="text-[#2F6DB5] hover:underline" data-testid={`doc-open-${doc.id}`}>{doc.filename || 'file'}</a>
                      {doc.person_name && <span className="text-[11px] text-slate-500">({doc.person_name})</span>}
                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${st.cls}`} data-testid={`doc-state-${doc.id}`}>{st.label}</span>
                      {doc.expiry && <span className="text-[11px] text-slate-500">exp {doc.expiry}</span>}
                      {doc.signed_date && <span className="text-[11px] text-slate-500">signed {doc.signed_date}</span>}
                      {canManage && (
                        <span className="ml-auto flex items-center gap-2">
                          {doc.verification_status !== 'verified'
                            ? <button type="button" onClick={() => setPreviewDoc(doc)} data-testid={`doc-verify-${doc.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:underline"><Eye size={13} /> Review &amp; verify</button>
                            : <button type="button" onClick={() => verify(doc, 'pending')} data-testid={`doc-unverify-${doc.id}`} className="text-xs text-slate-500 hover:underline">Unverify</button>}
                          <button type="button" onClick={() => remove(doc)} data-testid={`doc-remove-${doc.id}`} className="text-rose-500 hover:text-rose-700"><Trash2 size={13} /></button>
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {canManage && (
              <div className="mt-1.5 flex flex-wrap items-end gap-2">
                {section.person && (
                  <label className="text-xs text-slate-600">Person
                    <input value={d.person_name || ''} onChange={(e) => setDraftFor(section.type, { person_name: e.target.value })} data-testid={`${idPrefix}doc-person-${s}`} placeholder="License holder" className="ml-1 rounded border border-slate-200 px-1.5 py-1 text-sm" />
                  </label>
                )}
                {section.expiry && (
                  <label className="text-xs text-slate-600">Expiry
                    <input type="date" value={d.expiry || ''} onChange={(e) => setDraftFor(section.type, { expiry: e.target.value })} data-testid={`${idPrefix}doc-expiry-${s}`} className="ml-1 rounded border border-slate-200 px-1.5 py-1 text-sm" />
                  </label>
                )}
                {section.signed && (
                  <label className="text-xs text-slate-600">Signed date
                    <input type="date" value={d.signed_date || ''} onChange={(e) => setDraftFor(section.type, { signed_date: e.target.value })} data-testid={`${idPrefix}doc-signed-${s}`} className="ml-1 rounded border border-slate-200 px-1.5 py-1 text-sm" />
                  </label>
                )}
                <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md bg-[#0E2A47] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#16324F]">
                  <Upload size={14} />{busyType === section.type ? 'Uploading…' : `Upload ${section.type}`}
                  <input type="file" className="hidden" onChange={(e) => onFile(section, e)} disabled={!!busyType} data-testid={`${idPrefix}doc-upload-${s}`} />
                </label>
                {section.type === 'NDA' && (
                  <button type="button" onClick={() => setNdaEsign(true)} data-testid={`${idPrefix}doc-esign-${s}`}
                    className="inline-flex items-center gap-1.5 rounded-md border border-[#0E2A47] px-3 py-1.5 text-sm font-semibold text-[#0E2A47] hover:bg-[#0E2A47]/5">
                    <FileSignature size={14} /> E-Sign in-app
                  </button>
                )}
                {section.type === 'W-9' && (
                  <button type="button" onClick={() => setW9Esign(true)} data-testid={`${idPrefix}doc-esign-${s}`}
                    className="inline-flex items-center gap-1.5 rounded-md border border-[#0E2A47] px-3 py-1.5 text-sm font-semibold text-[#0E2A47] hover:bg-[#0E2A47]/5">
                    <FileSignature size={14} /> Fill &amp; sign in-app
                  </button>
                )}
              </div>
            )}
            <p className="mt-1 text-[11px] text-slate-400">{section.note}</p>
          </div>
        );
      })}
      {ndaEsign && (
        <ProviderNdaEsignDialog provider={provider} token={token}
          onClose={() => setNdaEsign(false)} onSigned={(updated) => onChanged?.(updated)} />
      )}
      {w9Esign && (
        <ProviderW9EsignDialog provider={provider} token={token}
          onClose={() => setW9Esign(false)} onSigned={(updated) => onChanged?.(updated)} />
      )}
      {previewDoc && (
        <Dialog open onOpenChange={() => setPreviewDoc(null)}>
          <DialogContent className="max-w-3xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="doc-verify-preview-dialog">
            <div className="flex items-center gap-2">
              <Eye size={18} className="text-[#0E2A47]" />
              <h3 className="text-lg font-bold text-[#0E2A47]">Review before verifying</h3>
            </div>
            <p className="text-xs text-slate-500">
              {previewDoc.type}{previewDoc.esigned ? ' · e-signed in-app' : ''}{previewDoc.person_name ? ` · ${previewDoc.person_name}` : ''}
              {previewDoc.esign?.tin_last4 ? ` · TIN •••••${previewDoc.esign.tin_last4}` : ''}
              {' '}— confirm the document is correct, then mark it verified. Verifying is what counts toward qualification.
            </p>
            <div className="min-h-0 flex-1 overflow-auto rounded-md border border-slate-200 bg-slate-50">
              {/\.(png|jpe?g|webp|gif|bmp|heic)$/i.test(previewDoc.filename || '')
                ? <img src={fileUrl(previewDoc)} alt={previewDoc.type} className="mx-auto max-h-[60vh] object-contain" data-testid="doc-preview-image" />
                : <iframe src={fileUrl(previewDoc)} title="Document preview" className="h-[60vh] w-full" data-testid="doc-preview-frame" />}
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
              <a href={fileUrl(previewDoc)} target="_blank" rel="noreferrer" data-testid="doc-preview-open-tab"
                className="mr-auto text-xs font-semibold text-[#2F6DB5] hover:underline">Open in new tab</a>
              <button type="button" onClick={() => setPreviewDoc(null)} data-testid="doc-preview-close"
                className="rounded-md border border-slate-200 px-4 py-2 text-sm">Close</button>
              <button type="button" onClick={() => verify(previewDoc, 'verified')} disabled={verifying} data-testid="doc-preview-confirm-verify"
                className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
                <CheckCircle2 size={15} /> {verifying ? 'Saving…' : 'Mark verified'}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
